import {
    AreaChartOutlined,
    DashboardOutlined,
    HomeOutlined,
    MobileOutlined,
    SettingOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';

import PageDashboard from '@/pages/dashboard';
import PageUsers from '@/pages/users/users';
import PageUsersOverview from '@/pages/users/overview';
import PageRegistrationChannels from '@/pages/users/registration-channels';
import PageFeaturesOverview from '@/pages/dashboard/features-overview';

import PageFinanceCapitalOperationRecords from '@/pages/finance/capital-operation-records';

import PageServiceConfig from '@/pages/service-config';
import PageServiceConfigDetail from '@/pages/service-config/detail';
import PageClientBanner from '@/pages/client-banner';
import PageAccountManagement from '@/pages/system-management/account-management';
import PageRoleManagement from '@/pages/system-management/role-management';
import PageLogs from '@/pages/system-management/logs';
import PageExports from '@/pages/system-management/exports';
import PageDramaReview from '@/pages/drama-review';
import PageUgcContentReports from '@/pages/ugc-reports/content';
import PageUgcUserReports from '@/pages/ugc-reports/users';
import PageUgcCommentReports from '@/pages/ugc-reports/comments';
import PageComments from '@/pages/comments';
import PageMiningWeeklyRewards from '@/pages/mining-weekly-rewards';
import PageMiningWeeklyRewardDetail from '@/pages/mining-weekly-rewards/detail';
import PageShareReview from '@/pages/activity-1011/share-review';
import PageUserStories from '@/pages/activity-1011/user-stories';
import PageActivity1011TaskConfig from '@/pages/activity-1011/task-config';
import PageAppVersionUpdates from '@/pages/app-version-updates';
import PageContentFilter from '@/pages/content-filter';

import type { MenuDataItem } from '@ant-design/pro-components';

const routes = [
    {
        element: <PageDashboard />,
        icon: <HomeOutlined />,
        name: (<FormattedMessage id="m.Home" />) as unknown as string,
        path: '/',
    },
    {
        children: [
            {
                access: 'Dramas_Dramas',
                element: <PageDramaReview />,
                name: <FormattedMessage id="m.Dramas_Dramas" />,
                path: 'drama-review',
            },
            {
                access: 'Dramas_Comments',
                element: <PageComments />,
                name: <FormattedMessage id="m.Dramas_Comments" />,
                path: 'comments',
            },
            {
                access: 'Dramas_UGC-Reports',
                name: <FormattedMessage id="m.Dramas_Reports" />,
                path: 'reports',
                children: [
                    {
                        access: 'Dramas_UGC-Reports',
                        element: <PageUgcContentReports />,
                        name: <FormattedMessage id="m.Dramas_Reports_Content" />,
                        path: 'content',
                    },
                    {
                        access: 'Dramas_UGC-Reports',
                        element: <PageUgcUserReports />,
                        name: <FormattedMessage id="m.Dramas_Reports_Users" />,
                        path: 'users',
                    },
                    {
                        access: 'Dramas_UGC-Reports',
                        element: <PageUgcCommentReports />,
                        name: <FormattedMessage id="m.Dramas_Reports_Comments" />,
                        path: 'comments',
                    },
                ],
            },
            {
                access: 'Dramas_Mining-Weekly-Rewards',
                element: <PageMiningWeeklyRewards />,
                name: <FormattedMessage id="m.Dramas_Mining-Weekly-Rewards" />,
                path: 'mining-weekly-rewards',
            },
            {
                access: 'Dramas_Mining-Weekly-Rewards',
                element: <PageMiningWeeklyRewardDetail />,
                hideInMenu: true,
                name: <FormattedMessage id="m.Dramas_Mining-Weekly-Rewards" />,
                path: 'mining-weekly-rewards/detail',
            },
            {
                access: 'Dramas_Activity-1011',
                name: <FormattedMessage id="m.Dramas_Activity-1011" />,
                path: 'activity-1011',
                children: [
                    {
                        access: 'Dramas_Activity-1011_Share-Review',
                        element: <PageShareReview />,
                        name: <FormattedMessage id="m.Dramas_Activity-1011_Share-Review" />,
                        path: 'share-review',
                    },
                    {
                        access: 'Dramas_Activity-1011_User-Stories',
                        element: <PageUserStories />,
                        name: <FormattedMessage id="m.Dramas_Activity-1011_User-Stories" />,
                        path: 'user-stories',
                    },
                    {
                        access: 'Dramas_Activity-1011_Task-Config',
                        element: <PageActivity1011TaskConfig />,
                        name: <FormattedMessage id="m.Dramas_Activity-1011_Task-Config" />,
                        path: 'task-config',
                    },
                ],
            },
        ],
        access: 'Dramas',
        icon: <VideoCameraOutlined />,
        name: <FormattedMessage id="m.Dramas" />,
        path: 'dramas',
    },
    {
        children: [
            {
                access: 'Client-Config_App-Version',
                element: <PageAppVersionUpdates />,
                name: <FormattedMessage id="m.Client-Management_App-Version-Updates" />,
                path: 'app-version-updates',
            },
            {
                access: 'Client-Config_Content-Filter',
                element: <PageContentFilter />,
                name: <FormattedMessage id="m.Client-Management_Content-Filter" />,
                path: 'content-filter',
            },
            {
                access: 'Dramas_Banner',
                element: <PageClientBanner />,
                name: <FormattedMessage id="m.Client-Management_Banner" />,
                path: 'banner',
            },
        ],
        icon: <MobileOutlined />,
        name: <FormattedMessage id="m.Client-Management" />,
        path: 'client-management',
    },
    {
        children: [
            {
                access: 'Users_Overview',
                element: <PageUsersOverview />,
                name: <FormattedMessage id="m.Users_Overview" />,
                path: 'overview',
            },
            {
                access: 'Dashboard_Features_Overview',
                element: <PageFeaturesOverview />,
                name: <FormattedMessage id="m.Dashboard_Features_Overview" />,
                path: 'features-overview',
            },
        ],
        icon: <DashboardOutlined />,
        name: <FormattedMessage id="m.Dashboard" />,
        path: 'dashboard',
    },
    {
        children: [
            {
                access: 'Users_Users',
                element: <PageUsers />,
                name: <FormattedMessage id="m.Users_Users" />,
                path: 'users',
            },
            {
                access: 'Users_Registration-Channels',
                element: <PageRegistrationChannels />,
                name: <FormattedMessage id="m.Users_Registration-Channels" />,
                path: 'registration-channels',
            },
        ],
        access: 'Users',
        icon: <AreaChartOutlined />,
        name: <FormattedMessage id="m.Users" />,
        path: 'users',
    },
    {
        children: [
            {
                access: 'Financial-Management_Capital-Operation-Records',
                element: <PageFinanceCapitalOperationRecords />,
                name: <FormattedMessage id="m.Financial-Management_Capital-Operation-Records" />,
                path: 'capital-operation-records',
            },
        ],
        icon: <AreaChartOutlined />,
        name: <FormattedMessage id="m.Financial-Management" />,
        access: 'Financial-Management',
        path: 'financial-management',
    },
    {
        children: [
            {
                access: 'System-Management_Configs',
                element: <PageServiceConfig />,
                name: <FormattedMessage id="m.Service-Config_Service-Config" />,
                path: 'service-config',
            },
            {
                access: 'System-Management_Configs',
                element: <PageServiceConfigDetail />,
                hideInMenu: true,
                name: <FormattedMessage id="m.Service-Config_Service-Config" />,
                path: 'service-config/detail',
            },
            {
                access: 'System-Management_Accounts',
                element: <PageAccountManagement />,
                name: <FormattedMessage id="m.System-Management_Accounts" />,
                path: 'accounts',
            },
            {
                access: 'System-Management_Roles',
                element: <PageRoleManagement />,
                name: <FormattedMessage id="m.System-Management_Roles" />,
                path: 'roles',
            },
            {
                access: 'System-Management_System-Logs',
                element: <PageLogs />,
                name: <FormattedMessage id="m.System-Management_System-Logs" />,
                path: 'logs',
            },
            {
                access: 'System-Management_Downloads',
                element: <PageExports />,
                name: <FormattedMessage id="m.System-Management_Exports" />,
                path: 'exports',
            },
        ],
        icon: <SettingOutlined />,
        name: <FormattedMessage id="m.System-Management" />,
        access: 'System-Management',
        path: 'system-management',
    },
] as MenuDataItem[];

export default routes;
